import './PHQList.css';
import { IonAlert, IonButton, IonCard, IonCardContent, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPopover, IonRow, IonTitle, IonToolbar, IonSearchbar, IonPage, IonButtons, IonSelect, IonSelectOption, IonMenuButton, useIonViewDidEnter } from '@ionic/react';
import { QuestionnaireCopyModel, QuestionnaireModel } from 'models/questionnaire.model';
import React, { useCallback, useContext, useState } from 'react';
import { ellipsisVertical, eyeOutline, trashOutline, createOutline, copyOutline, cloudDownloadOutline, addOutline } from 'ionicons/icons';
import { deletePHQ, getPHQByEnvironment, updatePHQ, copyPHQ, exportForm } from 'misc/phqHTTP';
import { AppContext } from 'context/app.context';
import FadeIn from '../animations/fadeIn';
import { QuestionnaireActionTypes } from 'reducers/questionnaire.reducer';
import { useHistory } from 'react-router';
import NewPHQ from './NewPHQ';
import { AxiosResponse } from 'axios';
import { HttpStatusCodeEnum } from 'models/http-status-code.enum';
import RemoveAlert from '../UI/RemoveAlert';
import Footer from 'layout/footer';


const PHQlist = () => {
  const history = useHistory();
  const { updateQuestionnaire, questionnaire, domain, setIsLoading } = useContext(AppContext);
  const phqList = questionnaire.PhqList;
  const domainCode = domain.Code;
  const [phqToUpdate, updatePHQToUpdate] = useState({} as QuestionnaireModel);
  const [showAlertToDelete, setAlertToDelete] = useState(false);
  const [showAlertToRename, setAlertToRename] = useState(false);
  const [showAlertToCopy, setAlertToCopy] = useState(false);
  const [isOpenModelInfo, updateModalStatus] = useState(false);
  const [searchedText, setSearchedText] = useState('');
  const [formActionsPopover, setShowPopover] = useState<{ open: boolean, event: Event | undefined }>({
    open: false,
    event: undefined,
  });

  const sortBy = {
    az: (a: QuestionnaireModel, b: QuestionnaireModel) => a.Label?.localeCompare(b.Label),
    za: (a: QuestionnaireModel, b: QuestionnaireModel) => b.Label?.localeCompare(a.Label),
    newest: (a: QuestionnaireModel, b: QuestionnaireModel) => Date.parse((b.UpdatedOn || b.CreatedOn)!) - Date.parse((a.UpdatedOn || a.CreatedOn)!),
    oldest: (a: QuestionnaireModel, b: QuestionnaireModel) => Date.parse((a.UpdatedOn || a.CreatedOn)!) - Date.parse((b.UpdatedOn || b.CreatedOn)!)
  }

  const [sortOrder, setSortOrder] = useState<keyof typeof sortBy>('newest');

  const PHQSelected = (phq: QuestionnaireModel) => {
    updateQuestionnaire(QuestionnaireActionTypes.PHQ_SELECTED, phq);
    history.push(`/phq/${phq.PHQID}`);
  };

  const getPHQ = useCallback(
    () => {
      setIsLoading(true);
      getPHQByEnvironment(domainCode)
        .then(res => updateQuestionnaire(QuestionnaireActionTypes.UPDATE_PHQLIST, res.data.result))
        .finally(() => { setIsLoading(false)})
    },
    [domainCode, updateQuestionnaire, setIsLoading]
  );

  const phqUpdateCallBack = (res: AxiosResponse<any>, setFunc: Function) => {
    if (res.status === HttpStatusCodeEnum.OK || res.status === HttpStatusCodeEnum.CREATED) {
      updatePHQToUpdate({} as QuestionnaireModel);
      setFunc();
      getPHQ();
    }
  };

  const renamePHQCallBack = (label: string) => {
    phqToUpdate.Label = label;
    updatePHQ(phqToUpdate).then(res => phqUpdateCallBack(res, setAlertToRename.bind(null, false)));
  };

  const copyPHQCallBack = (label: string) => {
    const copyPHQModel = {
      PHQID: phqToUpdate.PHQID,
      Label: label
    } as QuestionnaireCopyModel;
    copyPHQ(copyPHQModel).then(res => phqUpdateCallBack(res, setAlertToCopy.bind(null, false)));
  };

  const deletePHQCallBack = () => {
    deletePHQ(phqToUpdate.PHQID).then(res => phqUpdateCallBack(res, setAlertToDelete.bind(null, false)));
  };

  const updatePopOverstatus = (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>, page: React.SetStateAction<QuestionnaireModel>) => {
    setShowPopover({ open: true, event: e.nativeEvent })
    updatePHQToUpdate(page);
  };

  const callRename = (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>) => {
    setShowPopover({ open: false, event: e.nativeEvent })
    setAlertToRename(true)
  };

  const callCopy = (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>) => {
    setShowPopover({ open: false, event: e.nativeEvent })
    setAlertToCopy(true)
  };

  const callDelete = (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>) => {
    setShowPopover({ open: false, event: e.nativeEvent })
    setAlertToDelete(true)
  };

  const callView = (e: React.MouseEvent<HTMLIonItemElement, MouseEvent>) => {
    setShowPopover({ open: false, event: e.nativeEvent })
    PHQSelected(phqToUpdate);
  };

  const callDownload = (e: any) => {
    exportForm(phqToUpdate.PHQID).then(res => {

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(res.data);
      link.download = phqToUpdate.Name;
      link.click()
      link.remove();

      setShowPopover({ open: false, event: e.nativeEvent });
    });
  }

  useIonViewDidEnter(() => {
    getPHQ();
  })

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar color='primary'>
          <IonTitle className="ion-text-center">iPro Health - {domain?.Name}</IonTitle>
          <IonButton fill="clear" slot="start">
              <IonMenuButton menu="adminMenu" color="light"></IonMenuButton>
          </IonButton>
        </IonToolbar>
        <IonToolbar className="pt-2">
            <h2 className="px-2"><b>Patient Questionnaire List</b></h2>
            <IonItem className="ion-no-padding" slot="end" lines="none">
              <IonSelect  interface="popover" slot="end" value={sortOrder} onIonChange={e => setSortOrder(e.detail.value)}>
                <IonSelectOption value="az">A to Z</IonSelectOption>
                <IonSelectOption value="za">Z to A</IonSelectOption>
                <IonSelectOption value="newest">Newest</IonSelectOption>
                <IonSelectOption value="oldest">Oldest</IonSelectOption>
              </IonSelect>
              <IonButton slot="end" expand="block" size="small" onClick={(event) => { updateModalStatus(true); event.stopPropagation() }}>
                <b>Add New Form</b><IonIcon icon={addOutline} />
              </IonButton>
            </IonItem>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar debounce={300} value={searchedText} onIonChange={e => setSearchedText(e.detail.value!)}></IonSearchbar>
        </IonToolbar>
      </IonHeader>
      <IonContent id="maincontent">
        <IonPopover isOpen={formActionsPopover.open} event={formActionsPopover.event} onDidDismiss={() => setShowPopover({ open: false, event: undefined })}>
          <IonList>
            <IonItem button detail onClick={callView} lines="full">
              <IonButtons slot="start">
                <IonButton fill="clear" >
                  <IonIcon icon={eyeOutline} />
                </IonButton>
              </IonButtons>
              <strong>View</strong>
            </IonItem>
            <IonItem button detail onClick={callRename} lines="full">
              <IonButtons slot="start">
                <IonButton fill="clear" >
                  <IonIcon icon={createOutline} />
                </IonButton>
              </IonButtons>
              <strong>Rename</strong>
            </IonItem>
            <IonItem button detail onClick={callCopy} lines="full">
              <IonButtons slot="start">
                <IonButton fill="clear" >
                  <IonIcon icon={copyOutline} />
                </IonButton>
              </IonButtons>
              <strong>Copy</strong> 
            </IonItem>
            <IonItem button detail onClick={callDownload} lines="full">
              <IonButtons slot="start">
                <IonButton fill="clear">
                  <IonIcon icon={cloudDownloadOutline} />
                </IonButton>
              </IonButtons>
              <strong>Download</strong> 
            </IonItem>
            <IonItem button detail onClick={callDelete} lines="full">
              <IonButtons slot="start">
                <IonButton fill="clear" >
                  <IonIcon icon={trashOutline} />
                </IonButton>
              </IonButtons>
              <strong>Delete</strong>
            </IonItem>
          </IonList>
        </IonPopover>
        {isOpenModelInfo && (
          <NewPHQ fnToHideForm={updateModalStatus} fnUpdateList={getPHQ} />
        )}

        <RemoveAlert isOpen={showAlertToDelete} onCancel={() => setAlertToDelete(false)} onDelete={() => deletePHQCallBack()} />

        <IonAlert isOpen={showAlertToRename} onDidDismiss={() => setAlertToRename(false)} header={'Confirm'}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {
                setAlertToRename(false)
              }
            },
            {
              text: 'Yes',
              handler: (alertData) => {
                renamePHQCallBack(alertData.phqName);
              }
            }
          ]}
          inputs={[
            {
              name: 'phqName',
              type: 'text',
              placeholder: 'Enter new name'
            }
          ]}
        />

        <IonAlert isOpen={showAlertToCopy} onDidDismiss={() => setAlertToCopy(false)} header={'Confirm'}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {
                setAlertToCopy(false)
              }
            },
            {
              text: 'Yes',
              handler: (alertData) => {
                copyPHQCallBack(alertData.phqName);
              }
            }
          ]}
          inputs={[
            {
              name: 'phqName',
              type: 'text',
              placeholder: 'Enter name',
              value: `Copy of ${phqToUpdate.Label}`
            }
          ]}
        />
        
        <div className={`main-content ${isOpenModelInfo ? "add-mode" : ""}`}>

          <IonGrid className='pages-container'>
            <IonRow className='ion-no-padding'>
              {phqList
              ?.filter((f:QuestionnaireModel) => {
                if (!f.IsActive) return false;
                if (!searchedText) return true;
                return f.Label?.toLowerCase().indexOf(searchedText.toLowerCase()) > -1;
              })
              .sort(sortBy[sortOrder])
              .map((form: QuestionnaireModel) => {
                return (
                  <IonCol sizeSm='1' sizeMd='2' sizeLg='2' sizeXl='2' key={form.PHQID}>
                      <FadeIn key={form.PHQID}>
                        <IonCard className='form-card mt-0'>
                          <IonToolbar class="ion-no-border">
                            <IonButtons slot="end">
                              <IonButton className="ipro-btn" color="medium" fill="clear" onClick={(e) => updatePopOverstatus(e, form)}>
                                <IonIcon slot="end" icon={ellipsisVertical} />
                              </IonButton>
                            </IonButtons>
                          </IonToolbar>
                          <IonCardContent onClick={() => PHQSelected(form)} className=" card-list-container">
                            <div className="ion-text-center">
                              <IonButton className="add-form-btn ion-no-margin" size='small' fill="clear">
                                <IonLabel><p className="card-text">{form.Description || 'Description goes here'}</p></IonLabel>
                              </IonButton>
                            </div>
                            <div className='ion-text-center form-name-container'>
                              <IonLabel><h2><strong>{form.PHQID} - {form.Label}</strong></h2></IonLabel>
                            </div>
                          </IonCardContent>
                        </IonCard>
                    </FadeIn>
                  </IonCol>
                )
              })}
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
      
      <Footer />
    </IonPage>
  );
};
export default PHQlist;
